import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import type { StackedNumbersList } from 'src/components/followUp/common/types';

type UseExportOptionsProps = {
  data: StackedNumbersList | null;
  comparisonData: StackedNumbersList | null;
  stackLabels: { name: string }[];
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const useMapRows = () => {
  const { t } = useTranslation('followUp');

  return useCallback(
    (data: StackedNumbersList | null): Row[] => {
      if (!data) return [];
      const totals = data.results.reduce(
        (acc, { stacks }) => {
          stacks.forEach(({ stackLabel, value }) => {
            if (stackLabel === 'detractor') {
              acc.detractor += value;
            } else if (stackLabel === 'passive') {
              acc.passive += value;
            } else if (stackLabel === 'promoter') {
              acc.promoter += value;
            }
          });

          return acc;
        },
        { detractor: 0, passive: 0, promoter: 0 },
      );

      return [
        [
          {},
          { value: t('npsGroup.exportLabels.detractorN') },
          { value: t('npsGroup.exportLabels.detractorP') },
          { value: t('npsGroup.exportLabels.passiveN') },
          { value: t('npsGroup.exportLabels.passiveP') },
          { value: t('npsGroup.exportLabels.promoterN') },
          { value: t('npsGroup.exportLabels.promoterP') },
        ],
        ...data.results.map<Row>(({ label, stacks }) => {
          const detractorValue =
            stacks.find(({ stackLabel }) => stackLabel === 'detractor')
              ?.value ?? 0;
          const passiveValue =
            stacks.find(({ stackLabel }) => stackLabel === 'passive')?.value ??
            0;
          const promoterValue =
            stacks.find(({ stackLabel }) => stackLabel === 'promoter')?.value ??
            0;

          return [
            { value: t(`status.${label}`), fontWeight: 'bold' },
            {
              value: detractorValue,
            },
            {
              value: `${getRelativeValue(detractorValue, data.totalObjects, 0)}%`,
              align: 'right',
            },
            {
              value: passiveValue,
            },
            {
              value: `${getRelativeValue(passiveValue, data.totalObjects, 0)}%`,
              align: 'right',
            },
            {
              value: promoterValue,
            },
            {
              value: `${getRelativeValue(promoterValue, data.totalObjects, 0)}%`,
              align: 'right',
            },
          ];
        }),
        [
          {
            value: t('npsGroup.exportLabels.total'),
            fontWeight: 'bold',
          },
          {
            value: totals.detractor,
          },
          {
            value: `${getRelativeValue(totals.detractor, data.totalObjects, 0)}%`,
            align: 'right',
          },
          {
            value: totals.passive,
          },
          {
            value: `${getRelativeValue(totals.passive, data.totalObjects, 0)}%`,
            align: 'right',
          },
          {
            value: totals.promoter,
          },
          {
            value: `${getRelativeValue(totals.promoter, data.totalObjects, 0)}%`,
            align: 'right',
          },
        ],
      ];
    },
    [t],
  );
};

export const useExportOptions = ({
  data,
  comparisonData,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions => {
  const mapRows = useMapRows();

  return useMemo(() => {
    const rows = mapRows(data);
    const comparisonRows = comparisonData ? mapRows(comparisonData) : undefined;

    return {
      widgetName: widgetName,
      rows,
      comparisonRows,
      queryFilters,
    };
  }, [data, comparisonData, queryFilters, mapRows, widgetName]);
};
